import type {
  CustomInterrogationListItem,
  PartInterrogationFile,
} from '@frontend/api';

import type { ActionWithPayload } from 'store/types';

import type {
  InterrogationMaterialQuery,
  InterrogationSelection,
} from './types';

export const types = {
  CANCEL_EDIT_INTERROGATION_SETUP:
    'partLibrary/viewer/geometricInterrogation/CANCEL_EDIT_INTERROGATION_SETUP',
  CLEAR: 'partLibrary/viewer/geometricInterrogation/CLEAR',
  EDIT_INTERROGATION_SETUP:
    'partLibrary/viewer/geometricInterrogation/EDIT_INTERROGATION_SETUP',
  RECEIVE_QUERY: 'partLibrary/viewer/geometricInterrogation/RECEIVE_QUERY',
  RECEIVE_RESULT_FOR_DEFAULT:
    'partLibrary/viewer/geometricInterrogation/RECEIVE_RESULT_FOR_DEFAULT',
  RECEIVE_SEARCH_RESULTS:
    'partLibrary/viewer/geometricInterrogation/RECEIVE_SEARCH_RESULTS',
  SET_WITH_INTERROGATION_FILE:
    'partLibrary/viewer/geometricInterrogation/SET_WITH_INTERROGATION_FILE',
  SUBMIT: 'partLibrary/viewer/geometricInterrogation/SUBMIT',
} as const;

export type ReceiveQueryPayload = {
  forceInfo?: boolean;
  material: InterrogationMaterialQuery | null;
  task: string | null;
  shouldInit?: boolean;
  useSelectedTask?: boolean;
};

export type SetWithInterrogationFilePayload = {
  partFileUuid: string;
  partInterrogationFile: PartInterrogationFile;
  showAdditionalKeysExplicit: string[];
};

export type GeometricInterrogationAction =
  | ActionWithPayload<typeof types.CANCEL_EDIT_INTERROGATION_SETUP>
  | ActionWithPayload<typeof types.CLEAR>
  | ActionWithPayload<typeof types.EDIT_INTERROGATION_SETUP>
  | ActionWithPayload<typeof types.RECEIVE_QUERY, ReceiveQueryPayload>
  | ActionWithPayload<
      typeof types.RECEIVE_RESULT_FOR_DEFAULT,
      { result: InterrogationSelection | null }
    >
  | ActionWithPayload<
      typeof types.RECEIVE_SEARCH_RESULTS,
      { results: CustomInterrogationListItem[] }
    >
  | ActionWithPayload<
      typeof types.SUBMIT,
      { result: CustomInterrogationListItem }
    >;

export const geometricInterrogation = {
  cancelEditInterrogationSetup: () => ({
    type: types.CANCEL_EDIT_INTERROGATION_SETUP,
  }),
  clear: () => ({
    type: types.CLEAR,
  }),
  editInterrogationSetup: () => ({
    type: types.EDIT_INTERROGATION_SETUP,
  }),
  receiveQuery: ({
    forceInfo,
    material,
    task,
    shouldInit,
    useSelectedTask,
  }: ReceiveQueryPayload) => ({
    type: types.RECEIVE_QUERY,
    payload: { forceInfo, material, task, shouldInit, useSelectedTask },
  }),
  receiveResultForDefault: ({
    result,
  }: {
    result: InterrogationSelection | null;
  }) => ({
    type: types.RECEIVE_RESULT_FOR_DEFAULT,
    payload: { result },
  }),
  receiveSearchResults: ({
    results,
  }: {
    results: CustomInterrogationListItem[];
  }) => ({
    type: types.RECEIVE_SEARCH_RESULTS,
    payload: { results },
  }),
  setWithInterrogationFile: ({
    partFileUuid,
    partInterrogationFile,
    showAdditionalKeysExplicit,
  }: SetWithInterrogationFilePayload) => ({
    type: types.SET_WITH_INTERROGATION_FILE,
    payload: {
      partFileUuid,
      partInterrogationFile,
      showAdditionalKeysExplicit,
    },
  }),
  submit: ({ result }: { result: CustomInterrogationListItem }) => ({
    type: types.SUBMIT,
    payload: { result },
  }),
};
